import React from 'react';
import PropTypes from 'prop-types';
import { Button, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import { buildFailureCsv, downloadTextFile, isFinalJob, jobProgress } from '../../utils/bulkUploadJob';

const useStyles = makeStyles(theme => ({
    root: {
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        border: '1px solid var(--dm-border, #e7ebf0)',
        borderRadius: 12,
        background: 'var(--contentColor, #fff)',
    },
    title: { fontSize: '0.875rem', fontWeight: 700, color: 'var(--dm-text-strong, #263548)' },
    muted: { fontSize: '0.78rem', color: 'var(--dm-muted, #647184)', marginTop: 2 },
    progress: { margin: theme.spacing(1, 0), height: 6 },
    stats: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), margin: theme.spacing(1, 0) },
    stat: {
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: '0.75rem',
        fontWeight: 700,
        background: 'var(--dm-row-alt, #f1f5f9)',
        color: 'var(--dm-text-strong, #263548)',
    },
    statFailed: { background: 'rgba(220, 38, 38, 0.1)', color: '#b91c1c' },
    statCreated: { background: 'rgba(22, 163, 74, 0.12)', color: '#15803d' },
    tableWrap: { maxHeight: 220, overflow: 'auto', border: '1px solid var(--dm-border, #e7ebf0)', borderRadius: 8, marginTop: theme.spacing(1) },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(1), marginTop: theme.spacing(1) },
}));

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const BulkUploadReport = ({ job, error, onClose }) => {
    const classes = useStyles();
    if (!job && !error) return null;
    if (error && !job) {
        return (
            <div className={classes.root} role="alert">
                <Typography className={classes.title} style={{ color: '#b91c1c' }}>Upload failed</Typography>
                <Typography className={classes.muted}>{error}</Typography>
            </div>
        );
    }

    const finished = isFinalJob(job);
    const percent = jobProgress(job);
    const failed = job.failed || [];

    return (
        <div className={classes.root} role="status" aria-live="polite">
            <Typography className={classes.title}>
                {finished
                    ? (job.state === 'failed' ? 'Upload stopped' : 'Upload finished')
                    : `Processing users${job.total ? `: ${job.processed} of ${job.total}` : '…'}`}
            </Typography>
            {!finished ? (
                <>
                    <LinearProgress className={classes.progress} variant={percent === null ? 'indeterminate' : 'determinate'} value={percent || 0} />
                    <Typography className={classes.muted}>You can keep working; this list refreshes when the upload finishes.</Typography>
                </>
            ) : null}
            {job.message ? <Typography className={classes.muted}>{job.message}</Typography> : null}
            <div className={classes.stats}>
                <span className={`${classes.stat} ${classes.statCreated}`}>{plural(job.created.length, 'created')}</span>
                {job.updated.length ? <span className={classes.stat}>{job.updated.length} updated</span> : null}
                {job.moved.length ? <span className={classes.stat}>{job.moved.length} moved from another project</span> : null}
                <span className={`${classes.stat}${failed.length ? ` ${classes.statFailed}` : ''}`}>{failed.length} failed</span>
            </div>
            {failed.length ? (
                <div className={classes.tableWrap}>
                    <Table size="small" stickyHeader aria-label="Rows that failed">
                        <TableHead>
                            <TableRow>
                                <TableCell>Row</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Reason</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {failed.map((row, index) => (
                                <TableRow key={`${row.row ?? index}-${row.email || row.username}`}>
                                    <TableCell>{row.row ?? '—'}</TableCell>
                                    <TableCell>{row.email || row.username || '—'}</TableCell>
                                    <TableCell>{row.reason || 'Upload failed'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : null}
            <div className={classes.actions}>
                {failed.length ? (
                    <Button size="small" startIcon={<GetAppIcon />} onClick={() => downloadTextFile('bulk-upload-errors.csv', buildFailureCsv(failed))}>
                        Download error report
                    </Button>
                ) : null}
                {finished && onClose ? <Button size="small" variant="outlined" onClick={onClose}>Close</Button> : null}
            </div>
        </div>
    );
};

BulkUploadReport.propTypes = {
    job: PropTypes.object,
    error: PropTypes.string,
    onClose: PropTypes.func,
};

export default BulkUploadReport;
